import { CustomButton } from "@/components/ui/Buttons";
import { useGetEventVideosQuery } from "@/redux/feature/calender/calenderApi";
import VideoCardSkeleton from "../VideoCardSkeleton";
import ProductCard from "../ProductCard";
import { useState } from "react";
import { VideoData } from "@/redux/feature/videoDashboard/videoDashboardType";
import ProductDetailsModal from "../productDetailsModal/ProductDetailsModal";
import Modal from "@/components/ui/Modal";
import SelectModal from "@/pages/ProductStructure/components/SelectModal/SelectModal";
import GrowthIcon from "@/assets/icons/GrowTopArrow.svg";
import { FilterType } from "./ViewAllVideos";

type EventAllVideosModalProps = {
  productId: string;
  event: {
    eventName: string;
    date: string;
    untilDate: number;
    campaignStatus: string;
    data: VideoData[];
  };
};

const EventAllVideosModal = ({
  event,
  productId,
}: EventAllVideosModalProps) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [filter, setFilter] = useState<FilterType>("all");

  const { data, isLoading } = useGetEventVideosQuery({
    event: event.eventName,
    productId: productId,
  });

  // const allVideos = [
  //   ...(data?.competitor_ads ?? []),
  //   ...(data?.top_ads ?? []),
  //   ...(data?.competitor_reels ?? []),
  // ];

  const allVideos = event?.data;

  const filteredVideos = allVideos?.filter((video: VideoData) => {
    if (filter === "all") return true;
    if (filter === "ad") return video.ad_or_organic === "Ad";
    if (filter === "organic") return video.ad_or_organic === "Organic";
    return true;
  });

  return (
    <section>
      <h3 className="text-2xl font-medium leading-none tracking-[0%] text-center mb-6">
        Event 🗓
      </h3>

      <div className="mb-6 flex justify-between items-start">
        <div>
          <div className="flex gap-x-3">
            <p className="text-[15px] text-[#777777] bg-[#F3F3F3] px-[5px] py-[3.5px] rounded-lg">
              {new Date(event.date).toLocaleDateString("en-US", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </p>
            <p
              className={`py-1 px-4 rounded-[10px] text-[15px] ${
                event?.untilDate > 0
                  ? "text-[#006DBA] bg-[#EDF7FF]"
                  : "text-[#9B1C1C] bg-[#FEE2E2]"
              }`}
            >
              {event?.untilDate > 0
                ? `in ${event?.untilDate} days`
                : `${Math.abs(event?.untilDate)} days ago`}
            </p>
          </div>
          <h3 className="text-xl font-medium leading-none tracking-normal mt-4 mb-3">
            {event.eventName}
          </h3>
          <div className="flex items-center gap-x-2">
            <p className="text-[#777777] text-[15px] leading-none tracking-normal">
              Campaign starts
            </p>
            <p className="text-[#0B8000] text-[15px] leading-none tracking-normal py-1 px-[7.5px] bg-[#E2FFE3] rounded-[10px] ">
              {event.campaignStatus}
            </p>
          </div>
        </div>
        <div className="text-end">
          <CustomButton
            onClick={() => setIsSelectModalOpen(true)}
            variant="outline"
            border="1px solid #FF6600"
            borderRadius="12px"
            padding="10px"
            color="#FF6600"
            fontSize="16px"
            fontWeight="500"
            className="w-[216px] leading-6 tracking-[1.5%]"
          >
            Place Order
          </CustomButton>
          {/* <CustomButton
            // onClick={() => setIsPreviousYearModalOpen(true)}
            variant="outline"
            border="1px solid #E4E7EC"
            borderRadius="12px"
            padding="12px 25px"
            color="#344054"
            fontSize="14px"
            fontWeight="500"
            className="flex gap-x-2 items-center mt-3"
          >
            <GrowthIcon />
            View Previous Year Outcomes
          </CustomButton> */}
        </div>
      </div>

      {/* Filtering */}
      <div className="flex gap-x-3 items-center mb-3">
        {[
          { label: "All", value: "all" },
          { label: "Organic ads", value: "organic" },
          { label: "Inorganic ads", value: "ad" },
        ].map(({ label, value }) => (
          <button
            key={value}
            onClick={() => setFilter(value as FilterType)}
            className={`text-xs leading-[18px] tracking-normal py-1.5 px-[14px] border rounded-lg transition ${
              filter === value
                ? "border-[#FF6600] bg-[#FFF5F0] text-[#FF6600] font-medium"
                : "border-[#E4E7EC] text-black hover:bg-gray-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <VideoCardSkeleton key={index} />
          ))}
        </div>
      )}

      {!isLoading && filteredVideos?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 border border-dashed border-[#E4E7EC] rounded-lg bg-[#FAFAFA]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 text-gray-400 mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8c.707 0 1.354.293 1.854.793l5.293 5.293a2 2 0 010 2.828l-5.293 5.293a2 2 0 01-2.828 0l-5.293-5.293a2 2 0 010-2.828l5.293-5.293A2.615 2.615 0 0112 8z"
            />
          </svg>
          <p className="text-gray-500 text-lg font-medium">
            No videos available for this event.
          </p>
          <p className="text-gray-400 text-sm mt-1 text-center max-w-xs">
            Once campaigns are added or videos are uploaded, they will appear
            here.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
        {filteredVideos?.length > 0 &&
          filteredVideos?.map((video: VideoData) => (
            <ProductCard
              key={video.id}
              video={video}
              currentlyPlaying={currentlyPlaying}
              setCurrentlyPlaying={setCurrentlyPlaying}
              setIsDetailsModalOpen={setIsDetailsModalOpen}
              setSelectedVideo={setSelectedVideo}
            />
          ))}
      </div>

      <Modal
        padding="12px"
        width="750px"
        isOpen={isDetailsModalOpen}
        setOpen={setIsDetailsModalOpen}
      >
        <ProductDetailsModal
          showGenerateScript={true}
          video={selectedVideo as VideoData}
          setIsDetailsModalOpen={setIsDetailsModalOpen}
          setIsSelectModalOpen={setIsSelectModalOpen}
        />
      </Modal>

      <Modal
        isOpen={isSelectModalOpen}
        setOpen={setIsSelectModalOpen}
        width="min(900px, 95vw)"
      >
        <SelectModal
          isOpen={isSelectModalOpen}
          setOpen={setIsSelectModalOpen}
        />
      </Modal>
    </section>
  );
};

export default EventAllVideosModal;
